import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Plus, Edit2, Trash2, Eye, EyeOff, Save, X, Users, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  slug: "", name: "", region: "", city: "",
  description_pt: "", description_en: "", description_es: "",
  logo_url: "", accent_color: "#C9A640", founded_year: new Date().getFullYear(),
  active: true,
};

const slugify = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

export default function AdminCommunities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/communities`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch { toast.error("Erro ao carregar."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const slug = slugify(creating.slug || creating.name);
    if (slug.length < 2) { toast.error("Slug inválido."); return; }
    try {
      await axios.post(`${API}/admin/communities`,
        { ...creating, slug, founded_year: Number(creating.founded_year) || null },
        { withCredentials: true });
      toast.success("Comunidade criada.");
      setCreating(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao criar."); }
  };

  const save = async () => {
    if (!editing) return;
    const { slug, ...rest } = editing;
    try {
      await axios.patch(`${API}/admin/communities/${slug}`,
        { ...rest, founded_year: Number(rest.founded_year) || null },
        { withCredentials: true });
      toast.success("Guardado.");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro."); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminar a comunidade "${c.name}"? Esta ação é IRREVERSÍVEL.`)) return;
    try {
      await axios.delete(`${API}/admin/communities/${c.slug}`, { withCredentials: true });
      toast.success("Eliminada.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro."); }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/admin/communities/${c.slug}`,
        { active: !c.active }, { withCredentials: true });
      load();
    } catch { toast.error("Erro."); }
  };

  const downloadCharter = async (c, lang = "pt") => {
    try {
      const r = await axios.get(`${API}/admin/communities/${c.slug}/charter.pdf?lang=${lang}`,
        { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `carta-comunidade-${c.slug}-${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Erro a gerar a Carta."); }
  };

  return (
    <div className="px-6 py-10 lg:px-12 lg:py-14" data-testid="admin-communities-page">
      <div className="overline">Comunidades</div>
      <div className="flex items-start justify-between gap-4 flex-wrap mt-3">
        <h1 className="display-heading text-4xl sm:text-5xl">Comunidades CIEES.</h1>
        {!creating && (
          <button onClick={() => setCreating(empty)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630]"
            data-testid="community-create-btn">
            <Plus className="h-4 w-4" /> Nova comunidade
          </button>
        )}
      </div>
      <span className="accent-rule mt-6 block" />
      <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
        Geram as comunidades regionais do CIEES Portugal (Lisboa, Porto, Coimbra…). Cada comunidade aparece na página pública <code className="text-xs bg-[hsl(var(--secondary))] px-1.5 py-0.5">/comunidades</code> e os sócios podem ser-lhe atribuídos a partir do diretório de sócios.
      </p>

      {creating && (
        <form onSubmit={create} className="mt-8 bg-white border border-[hsl(var(--border))] p-6 space-y-4" data-testid="community-create-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Nome *"><input required value={creating.name}
              onChange={(e) => setCreating({...creating, name: e.target.value,
                slug: creating.slug || slugify(e.target.value)})}
              className="form-input" data-testid="community-name" /></F>
            <F label="Slug (URL) *" hint="ex.: lisboa, porto-norte"><input required value={creating.slug}
              onChange={(e) => setCreating({...creating, slug: slugify(e.target.value)})}
              className="form-input font-mono text-sm" data-testid="community-slug" /></F>
            <F label="Região"><input value={creating.region}
              onChange={(e) => setCreating({...creating, region: e.target.value})}
              placeholder="ex.: Grande Lisboa, Norte" className="form-input" /></F>
            <F label="Cidade"><input value={creating.city}
              onChange={(e) => setCreating({...creating, city: e.target.value})}
              className="form-input" /></F>
            <F label="Ano de fundação"><input type="number" min="2000" max="2100" value={creating.founded_year}
              onChange={(e) => setCreating({...creating, founded_year: e.target.value})}
              className="form-input" /></F>
            <F label="Cor de destaque" hint="HEX (ex.: #C9A640)"><input type="color" value={creating.accent_color}
              onChange={(e) => setCreating({...creating, accent_color: e.target.value})}
              className="h-10 w-full border border-[hsl(var(--border))]" /></F>
          </div>
          <F label="Descrição (PT)" hint="Visível na página da comunidade.">
            <textarea rows={4} value={creating.description_pt}
              onChange={(e) => setCreating({...creating, description_pt: e.target.value})}
              className="form-input" /></F>
          <F label="URL do logótipo">
            <input value={creating.logo_url} onChange={(e) => setCreating({...creating, logo_url: e.target.value})}
              placeholder="https://…" className="form-input" /></F>
          <label className="flex items-center gap-2"><input type="checkbox" checked={creating.active}
            onChange={(e) => setCreating({...creating, active: e.target.checked})}
            className="h-4 w-4 accent-[hsl(var(--primary))]" /> <span className="text-sm">Ativa (visível no site público)</span></label>
          <div className="flex justify-end gap-3 pt-3 border-t border-[hsl(var(--border))]">
            <button type="button" onClick={() => setCreating(null)} className="px-4 py-2 text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Cancelar</button>
            <button type="submit" className="px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630]" data-testid="community-create-submit">Criar</button>
          </div>
        </form>
      )}

      <div className="mt-8 space-y-3">
        {loading ? (
          <p className="text-[hsl(var(--muted-foreground))]">A carregar…</p>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[hsl(var(--border))] p-8 text-center">
            <MapPin className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto" />
            <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Sem comunidades. Crie a primeira acima.</p>
          </div>
        ) : items.map((c) => (
          <div key={c.slug}
            className={`bg-white border p-5 ${c.active ? "border-[hsl(var(--border))]" : "border-[hsl(var(--border))] opacity-60"}`}
            data-testid={`community-row-${c.slug}`}>
            {editing?.slug === c.slug ? (
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <F label="Nome"><input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} className="form-input" /></F>
                  <F label="Região"><input value={editing.region || ""} onChange={(e) => setEditing({...editing, region: e.target.value})} className="form-input" /></F>
                  <F label="Cidade"><input value={editing.city || ""} onChange={(e) => setEditing({...editing, city: e.target.value})} className="form-input" /></F>
                  <F label="Ano de fundação"><input type="number" value={editing.founded_year || ""} onChange={(e) => setEditing({...editing, founded_year: e.target.value})} className="form-input" /></F>
                  <F label="Cor"><input type="color" value={editing.accent_color || "#C9A640"} onChange={(e) => setEditing({...editing, accent_color: e.target.value})} className="h-10 w-full border border-[hsl(var(--border))]" /></F>
                  <F label="Logótipo"><input value={editing.logo_url || ""} onChange={(e) => setEditing({...editing, logo_url: e.target.value})} className="form-input" /></F>
                </div>
                <F label="Descrição (PT)"><textarea rows={4} value={editing.description_pt || ""} onChange={(e) => setEditing({...editing, description_pt: e.target.value})} className="form-input" /></F>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] border border-[hsl(var(--border))]"><X className="inline h-3 w-3 mr-1" />Cancelar</button>
                  <button onClick={save} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] bg-[hsl(var(--primary))] text-white" data-testid={`community-save-${c.slug}`}><Save className="inline h-3 w-3 mr-1" />Guardar</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4 min-w-0 flex-1">
                  <div className="h-14 w-14 flex items-center justify-center border border-[hsl(var(--border))]" style={{ backgroundColor: c.accent_color + "20" }}>
                    {c.logo_url ? <img src={c.logo_url} alt="" className="h-14 w-14 object-contain" /> : <MapPin className="h-6 w-6" style={{ color: c.accent_color }} />}
                  </div>
                  <div className="min-w-0">
                    <h3 className="display-heading text-2xl text-[hsl(var(--primary))]">{c.name}</h3>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[hsl(var(--muted-foreground))]">
                      <span className="font-mono bg-[hsl(var(--secondary))] px-1.5 py-0.5">/{c.slug}</span>
                      {c.region && <span>{c.region}</span>}
                      {c.city && <span>· {c.city}</span>}
                      {c.founded_year && <span>· desde {c.founded_year}</span>}
                      <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {c.member_count} sócio(s)</span>
                    </div>
                    {c.description_pt && (
                      <p className="mt-2 text-sm text-[hsl(var(--foreground))] line-clamp-2 max-w-2xl">{c.description_pt}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <div className="flex items-center border border-[hsl(var(--border))]">
                    <span className="pl-2 pr-1 text-[hsl(var(--muted-foreground))]" title="Carta de Comunidade (PDF)">
                      <FileText className="h-4 w-4" />
                    </span>
                    {["pt", "en", "es"].map((lg) => (
                      <button key={lg} onClick={() => downloadCharter(c, lg)}
                        className="px-1.5 py-2 text-[10px] uppercase tracking-wider hover:text-[hsl(var(--accent))]"
                        title={`Descarregar Carta de Comunidade (${lg.toUpperCase()})`}
                        data-testid={`community-charter-${c.slug}-${lg}`}>
                        {lg}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => toggleActive(c)}
                    className="p-2 border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]"
                    title={c.active ? "Desativar" : "Ativar"}
                    data-testid={`community-toggle-${c.slug}`}>
                    {c.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />}
                  </button>
                  <button onClick={() => setEditing({ ...c })}
                    className="p-2 border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]"
                    title="Editar" data-testid={`community-edit-${c.slug}`}>
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(c)}
                    className="p-2 border border-red-200 text-red-700 hover:bg-red-50"
                    title="Eliminar" data-testid={`community-delete-${c.slug}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function F({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-[hsl(var(--muted-foreground))] mt-1 italic">{hint}</span>}
    </label>
  );
}
